import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Activity,
  CheckCircle2,
  XCircle,
  Repeat,
  Building2,
  UserCircle,
  Target,
  History,
  Globe,
  Linkedin,
  Mail,
  Phone,
  Save,
} from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';
import type { LeadDto, CrmActivityDto } from '@solara/api-client';

type LeadWithExtras = LeadDto & { activities?: CrmActivityDto[] };

const BANT_OPTIONS = {
  bantBudget: ['CONFIRMED', 'INDICATIVE', 'UNKNOWN', 'NONE'],
  bantAuthority: ['DECISION_MAKER', 'INFLUENCER', 'END_USER', 'UNKNOWN'],
  bantNeed: ['URGENT', 'MEDIUM', 'LOW', 'UNKNOWN'],
  bantTimeline: ['LT_3_MONTH', 'M3_6_MONTH', 'M6_12_MONTH', 'GT_12_MONTH', 'UNKNOWN'],
} as const;

const STATUS_TONES: Record<LeadDto['status'], 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  NEW: 'info',
  CONTACTED: 'warning',
  QUALIFIED: 'success',
  DISQUALIFIED: 'danger',
  CONVERTED: 'neutral',
};

const ACTIVITY_ICONS: Record<CrmActivityDto['activityType'], string> = {
  CALL: '📞',
  MEETING: '🗓️',
  EMAIL: '✉️',
  DEMO: '🖥️',
  PROPOSAL: '📄',
  NOTE: '📝',
};

interface EditableLead {
  subjectOfInterest: string;
  leadScore: number;
  estimatedValueAed: string;
  estimatedMrrAed: string;
  estimatedArrAed: string;
  expectedCloseDate: string;
  probability: string;
  lossReason: string;
  bantBudget: string;
  bantAuthority: string;
  bantNeed: string;
  bantTimeline: string;
  meddpiccMetrics: string;
  meddpiccEconomicBuyer: string;
  meddpiccDecisionCriteria: string;
  meddpiccDecisionProcess: string;
  meddpiccPaperProcess: string;
  meddpiccIdentifyPain: string;
  meddpiccChampion: string;
  meddpiccCompetition: string;
  notes: string;
}

function fromMinor(v: string | number | null | undefined): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n)) return '';
  return (n / 100).toString();
}

function toMinor(v: string): string | undefined {
  if (!v) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100).toString();
}

function buildEdit(lead: LeadDto): EditableLead {
  return {
    subjectOfInterest: lead.subjectOfInterest ?? '',
    leadScore: lead.leadScore ?? 0,
    estimatedValueAed: fromMinor(lead.estimatedValueMinor),
    estimatedMrrAed: fromMinor(lead.estimatedMrrMinor),
    estimatedArrAed: fromMinor(lead.estimatedArrMinor),
    expectedCloseDate: lead.expectedCloseDate?.slice(0, 10) ?? '',
    probability: lead.probability != null ? String(lead.probability) : '',
    lossReason: lead.lossReason ?? '',
    bantBudget: lead.bantBudget,
    bantAuthority: lead.bantAuthority,
    bantNeed: lead.bantNeed,
    bantTimeline: lead.bantTimeline,
    meddpiccMetrics: lead.meddpiccMetrics ?? '',
    meddpiccEconomicBuyer: lead.meddpiccEconomicBuyer ?? '',
    meddpiccDecisionCriteria: lead.meddpiccDecisionCriteria ?? '',
    meddpiccDecisionProcess: lead.meddpiccDecisionProcess ?? '',
    meddpiccPaperProcess: lead.meddpiccPaperProcess ?? '',
    meddpiccIdentifyPain: lead.meddpiccIdentifyPain ?? '',
    meddpiccChampion: lead.meddpiccChampion ?? '',
    meddpiccCompetition: lead.meddpiccCompetition ?? '',
    notes: lead.notes ?? '',
  };
}

export default function LeadDetailPage() {
  const { t } = useTranslation('p1');
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<LeadWithExtras | null>(null);
  const [edit, setEdit] = useState<EditableLead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({
    dealName: '',
    amountAed: '',
    expectedCloseDate: '',
    packageTier: 'GROWTH',
  });

  useEffect(() => {
    if (leadId) void load(leadId);
  }, [leadId]);

  async function load(id: string) {
    try {
      const fetched = (await crmApi.getLead(id)) as LeadWithExtras;
      setLead(fetched);
      setEdit(buildEdit(fetched));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  const company = lead?.company;

  const dirty = useMemo(() => {
    if (!lead || !edit) return false;
    const baseline = buildEdit(lead);
    return JSON.stringify(baseline) !== JSON.stringify(edit);
  }, [lead, edit]);

  async function save() {
    if (!lead || !edit) return;
    setBusy(true);
    setError(null);
    try {
      await crmApi.updateLead(lead.id, {
        subjectOfInterest: edit.subjectOfInterest || undefined,
        leadScore: edit.leadScore,
        estimatedValueMinor: toMinor(edit.estimatedValueAed),
        estimatedMrrMinor: toMinor(edit.estimatedMrrAed),
        estimatedArrMinor: toMinor(edit.estimatedArrAed),
        expectedCloseDate: edit.expectedCloseDate || undefined,
        probability: edit.probability ? Number(edit.probability) : undefined,
        lossReason: edit.lossReason || undefined,
        bantBudget: edit.bantBudget,
        bantAuthority: edit.bantAuthority,
        bantNeed: edit.bantNeed,
        bantTimeline: edit.bantTimeline,
        meddpiccMetrics: edit.meddpiccMetrics || undefined,
        meddpiccEconomicBuyer: edit.meddpiccEconomicBuyer || undefined,
        meddpiccDecisionCriteria: edit.meddpiccDecisionCriteria || undefined,
        meddpiccDecisionProcess: edit.meddpiccDecisionProcess || undefined,
        meddpiccPaperProcess: edit.meddpiccPaperProcess || undefined,
        meddpiccIdentifyPain: edit.meddpiccIdentifyPain || undefined,
        meddpiccChampion: edit.meddpiccChampion || undefined,
        meddpiccCompetition: edit.meddpiccCompetition || undefined,
        notes: edit.notes || undefined,
      });
      setSaved(true);
      await load(lead.id);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function transition(status: LeadDto['status']) {
    if (!lead) return;
    let reason: string | undefined;
    if (status === 'DISQUALIFIED') {
      reason = window.prompt(t('crm.disqualifyReasonPrompt')) ?? undefined;
      if (!reason) return;
    }
    setBusy(true);
    setError(null);
    try {
      await crmApi.transitionLead(lead.id, status, reason);
      await load(lead.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('crm.transitionFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function convertToOpportunity() {
    if (!lead) return;
    if (!convertForm.dealName || !convertForm.amountAed || !convertForm.expectedCloseDate) {
      setError(t('crm.fillConversionForm'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const opp = await crmApi.convertLead(lead.id, {
        ownerUserId: lead.assignedToUserId,
        dealName: convertForm.dealName,
        amountMinor: Math.round(Number(convertForm.amountAed) * 100),
        currency: 'AED',
        expectedCloseDate: convertForm.expectedCloseDate,
        packageTier: convertForm.packageTier,
      });
      navigate(`/crm/opportunities/${opp.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('crm.conversionFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (!lead || !edit) {
    return (
      <div className="p-6 text-sm text-fg-muted">{error ?? t('crm.loading')}</div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex items-center justify-between gap-4">
        <div>
          <Link
            to="/crm/leads"
            className="text-xs text-fg-muted hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3 rtl-mirror" /> {t('crm.backToLeads')}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {company?.companyName ?? t('crm.unknownCompany')}
          </h1>
          <p className="text-fg-muted text-sm">
            {company?.primaryContactFirstName} {company?.primaryContactLastName} ·{' '}
            {company?.primaryContactEmail}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={STATUS_TONES[lead.status]} className="text-xs">
            {t(`crm.leadStatuses.${lead.status}`, { defaultValue: lead.status })}
          </Badge>
          <Button onClick={() => void save()} disabled={busy || !dirty}>
            <Save className="mr-2 h-3 w-3" />
            {saved ? t('common.saved', { defaultValue: 'Saved' }) : t('actions.save')}
          </Button>
        </div>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {lead.duplicateSimilarityScore != null && lead.duplicateSimilarityScore > 70 && (
        <Card className="border-warning/40 bg-warning/5 rounded-2xl">
          <CardBody className="text-sm">
            {t('crm.duplicateOfLead', {
              id: lead.duplicateOfLeadId?.slice(0, 8) ?? '?',
              score: Number(lead.duplicateSimilarityScore),
            })}
          </CardBody>
        </Card>
      )}

      {/* Section 1 — Company data */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t('crm.sectionCompanyData', { defaultValue: 'Company data' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Field label={t('crm.companyName')} value={company?.companyName} />
          <Field label={t('crm.trn', { defaultValue: 'TRN' })} value={company?.trn} mono />
          <Field
            label={t('crm.registrationNumber', { defaultValue: 'Registration no.' })}
            value={company?.registrationNumber}
            mono
          />
          <Field
            label={t('crm.tradeLicense', { defaultValue: 'Trade license' })}
            value={company?.tradeLicenseNumber}
            mono
          />
          <Field
            label={t('crm.legalAddress', { defaultValue: 'Legal address' })}
            value={company?.legalAddress}
            wide
          />
          <Field
            label={t('crm.billingAddress', { defaultValue: 'Billing address' })}
            value={company?.billingAddress}
            wide
          />
          <Field
            label={t('crm.mailingAddress', { defaultValue: 'Mailing address' })}
            value={company?.mailingAddress}
            wide
          />
        </CardBody>
      </Card>

      {/* Section 2 — Firmographics */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('crm.sectionFirmographics', { defaultValue: 'Firmographics' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Field label={t('crm.industry')} value={company?.industry} />
          <Field
            label={t('crm.companySize', { defaultValue: 'Company size' })}
            value={company?.companySize}
          />
          <Field
            label={t('crm.annualRevenue', { defaultValue: 'Annual revenue (AED)' })}
            value={company?.annualRevenueAed}
          />
          <Field
            label={t('crm.customerType', { defaultValue: 'Customer type' })}
            value={company?.customerType}
          />
          <Field
            label={t('crm.accountTier', { defaultValue: 'Account tier' })}
            value={company?.accountTier}
          />
          <Field
            label={t('crm.timezone', { defaultValue: 'Timezone' })}
            value={company?.timezone}
          />
          <Field
            label={t('crm.parentCompany', { defaultValue: 'Parent company' })}
            value={company?.parentCompany?.companyName ?? company?.parentCompanyId}
          />
          <Field
            label={t('crm.accountOwner', { defaultValue: 'Account owner' })}
            value={company?.accountOwnerUserId}
          />
          {company?.websiteUrl && (
            <a
              href={company.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Globe className="h-3 w-3" /> {company.websiteUrl}
            </a>
          )}
          {company?.linkedinUrl && (
            <a
              href={company.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Linkedin className="h-3 w-3" /> {company.linkedinUrl}
            </a>
          )}
        </CardBody>
      </Card>

      {/* Section 3 — Contact */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="h-4 w-4" />
            {t('crm.sectionContact', { defaultValue: 'Primary contact' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Field
            label={t('crm.contactFirstName')}
            value={company?.primaryContactFirstName}
          />
          <Field
            label={t('crm.contactLastName')}
            value={company?.primaryContactLastName}
          />
          <Field
            label={t('crm.jobTitle', { defaultValue: 'Job title' })}
            value={company?.primaryContactJobTitle}
          />
          <Field
            label={t('crm.department', { defaultValue: 'Department' })}
            value={company?.primaryContactDepartment}
          />
          <Field
            label={t('crm.decisionRole', { defaultValue: 'Decision role' })}
            value={company?.primaryContactDecisionRole}
          />
          <Field
            label={t('crm.contactEmail')}
            value={company?.primaryContactEmail}
            icon={<Mail className="h-3 w-3" />}
          />
          <Field
            label={t('crm.emailAlt', { defaultValue: 'Alt. email' })}
            value={company?.primaryContactEmailAlt}
            icon={<Mail className="h-3 w-3" />}
          />
          <Field
            label={t('crm.contactPhone')}
            value={company?.primaryContactPhone}
            icon={<Phone className="h-3 w-3" />}
          />
          <Field
            label={t('crm.phoneDirect', { defaultValue: 'Direct phone' })}
            value={company?.primaryContactPhoneDirect}
            icon={<Phone className="h-3 w-3" />}
          />
          <Field
            label={t('crm.mobile', { defaultValue: 'Mobile' })}
            value={company?.primaryContactMobile}
            icon={<Phone className="h-3 w-3" />}
          />
          <Field
            label={t('crm.preferredLanguage', { defaultValue: 'Preferred language' })}
            value={company?.primaryContactPreferredLanguage}
          />
          <Field
            label={t('crm.preferredChannel', { defaultValue: 'Preferred channel' })}
            value={company?.primaryContactPreferredChannel}
          />
          {company?.primaryContactLinkedin && (
            <a
              href={company.primaryContactLinkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Linkedin className="h-3 w-3" /> {company.primaryContactLinkedin}
            </a>
          )}
          <Field
            label={t('crm.newsletterOptOut', { defaultValue: 'Newsletter opt-out' })}
            value={company?.newsletterOptOut ? t('common.yes', { defaultValue: 'Yes' }) : t('common.no', { defaultValue: 'No' })}
          />
          <Field
            label={t('crm.lastInteraction', { defaultValue: 'Last interaction' })}
            value={
              company?.lastInteractionAt ? new Date(company.lastInteractionAt).toLocaleString() : undefined
            }
          />
        </CardBody>
      </Card>

      {/* Section 4 — Lead score + BANT + MEDDPICC + deal forecast */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            {t('crm.sectionScoring', { defaultValue: 'Lead score + BANT + MEDDPICC' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-4 text-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">
                {t('crm.source', { defaultValue: 'Source' })}
              </span>
              <span className="rounded border border-border bg-surface-muted px-3 py-2 text-sm">
                {lead.leadSource}
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">
                {t('crm.subjectOfInterest', { defaultValue: 'Subject of interest' })}
              </span>
              <Input
                value={edit.subjectOfInterest}
                onChange={(e) => setEdit({ ...edit, subjectOfInterest: e.target.value })}
                placeholder={t('crm.subjectOfInterestPlaceholder', {
                  defaultValue: 'Package / module / custom build…',
                })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">
                {t('crm.leadScore', { defaultValue: 'Lead score (0–100)' })}
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={edit.leadScore}
                onChange={(e) => setEdit({ ...edit, leadScore: Number(e.target.value) })}
                className="accent-primary"
              />
              <span className="text-xs font-medium">{edit.leadScore}</span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              label={t('crm.bantBudget')}
              value={edit.bantBudget}
              options={BANT_OPTIONS.bantBudget}
              onChange={(v) => setEdit({ ...edit, bantBudget: v })}
            />
            <SelectField
              label={t('crm.bantAuthority')}
              value={edit.bantAuthority}
              options={BANT_OPTIONS.bantAuthority}
              onChange={(v) => setEdit({ ...edit, bantAuthority: v })}
            />
            <SelectField
              label={t('crm.bantNeed')}
              value={edit.bantNeed}
              options={BANT_OPTIONS.bantNeed}
              onChange={(v) => setEdit({ ...edit, bantNeed: v })}
            />
            <SelectField
              label={t('crm.bantTimeline')}
              value={edit.bantTimeline}
              options={BANT_OPTIONS.bantTimeline}
              onChange={(v) => setEdit({ ...edit, bantTimeline: v })}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label={t('crm.estimatedValue', { defaultValue: 'Estimated value (AED)' })}
              type="number"
              value={edit.estimatedValueAed}
              onChange={(e) => setEdit({ ...edit, estimatedValueAed: e.target.value })}
            />
            <Input
              label={t('crm.estimatedMrr', { defaultValue: 'MRR (AED)' })}
              type="number"
              value={edit.estimatedMrrAed}
              onChange={(e) => setEdit({ ...edit, estimatedMrrAed: e.target.value })}
            />
            <Input
              label={t('crm.estimatedArr', { defaultValue: 'ARR (AED)' })}
              type="number"
              value={edit.estimatedArrAed}
              onChange={(e) => setEdit({ ...edit, estimatedArrAed: e.target.value })}
            />
            <Input
              label={t('crm.expectedClose')}
              type="date"
              value={edit.expectedCloseDate}
              onChange={(e) => setEdit({ ...edit, expectedCloseDate: e.target.value })}
            />
            <Input
              label={t('crm.probability') + ' (%)'}
              type="number"
              value={edit.probability}
              onChange={(e) => setEdit({ ...edit, probability: e.target.value })}
            />
            <Input
              label={t('crm.lossReasonLabel')}
              value={edit.lossReason}
              onChange={(e) => setEdit({ ...edit, lossReason: e.target.value })}
            />
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            <p className="text-xs uppercase tracking-wide text-fg-muted">
              {t('crm.sectionMeddpicc', { defaultValue: 'MEDDPICC' })}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextareaField
                label={t('crm.meddpiccMetrics', { defaultValue: 'Metrics' })}
                value={edit.meddpiccMetrics}
                onChange={(v) => setEdit({ ...edit, meddpiccMetrics: v })}
              />
              <Input
                label={t('crm.meddpiccEconomicBuyer', { defaultValue: 'Economic buyer' })}
                value={edit.meddpiccEconomicBuyer}
                onChange={(e) => setEdit({ ...edit, meddpiccEconomicBuyer: e.target.value })}
              />
              <TextareaField
                label={t('crm.meddpiccDecisionCriteria', { defaultValue: 'Decision criteria' })}
                value={edit.meddpiccDecisionCriteria}
                onChange={(v) => setEdit({ ...edit, meddpiccDecisionCriteria: v })}
              />
              <TextareaField
                label={t('crm.meddpiccDecisionProcess', { defaultValue: 'Decision process' })}
                value={edit.meddpiccDecisionProcess}
                onChange={(v) => setEdit({ ...edit, meddpiccDecisionProcess: v })}
              />
              <TextareaField
                label={t('crm.meddpiccPaperProcess', { defaultValue: 'Paper process' })}
                value={edit.meddpiccPaperProcess}
                onChange={(v) => setEdit({ ...edit, meddpiccPaperProcess: v })}
              />
              <TextareaField
                label={t('crm.meddpiccIdentifyPain', { defaultValue: 'Identify pain' })}
                value={edit.meddpiccIdentifyPain}
                onChange={(v) => setEdit({ ...edit, meddpiccIdentifyPain: v })}
              />
              <Input
                label={t('crm.meddpiccChampion', { defaultValue: 'Champion' })}
                value={edit.meddpiccChampion}
                onChange={(e) => setEdit({ ...edit, meddpiccChampion: e.target.value })}
              />
              <Input
                label={t('crm.meddpiccCompetition', { defaultValue: 'Competition' })}
                value={edit.meddpiccCompetition}
                onChange={(e) => setEdit({ ...edit, meddpiccCompetition: e.target.value })}
              />
            </div>
          </div>

          <TextareaField
            label={t('crm.notes')}
            value={edit.notes}
            onChange={(v) => setEdit({ ...edit, notes: v })}
            rows={3}
          />
        </CardBody>
      </Card>

      {/* Lifecycle actions */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.lifecycle')}</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap gap-2 text-sm">
          {lead.status === 'NEW' && (
            <Button onClick={() => void transition('CONTACTED')} disabled={busy} variant="secondary">
              <Repeat className="mr-2 h-3 w-3" />
              {t('crm.markContacted')}
            </Button>
          )}
          {(lead.status === 'NEW' || lead.status === 'CONTACTED') && (
            <Button onClick={() => void transition('QUALIFIED')} disabled={busy}>
              <CheckCircle2 className="mr-2 h-3 w-3" />
              {t('crm.qualify')}
            </Button>
          )}
          {(lead.status === 'NEW' || lead.status === 'CONTACTED') && (
            <Button onClick={() => void transition('DISQUALIFIED')} disabled={busy} variant="ghost">
              <XCircle className="mr-2 h-3 w-3" />
              {t('crm.disqualify')}
            </Button>
          )}
          {lead.status === 'QUALIFIED' && !convertOpen && (
            <Button onClick={() => setConvertOpen(true)} disabled={busy}>
              <CheckCircle2 className="mr-2 h-3 w-3" />
              {t('crm.convertToOpportunity')}
            </Button>
          )}
        </CardBody>
        {convertOpen && lead.status === 'QUALIFIED' && (
          <CardBody className="border-t border-border space-y-3 text-sm">
            <Input
              label={t('crm.dealName')}
              value={convertForm.dealName}
              onChange={(e) => setConvertForm({ ...convertForm, dealName: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('crm.dealValue')}
                type="number"
                value={convertForm.amountAed}
                onChange={(e) => setConvertForm({ ...convertForm, amountAed: e.target.value })}
              />
              <Input
                label={t('crm.expectedCloseRequired')}
                type="date"
                value={convertForm.expectedCloseDate}
                onChange={(e) => setConvertForm({ ...convertForm, expectedCloseDate: e.target.value })}
              />
            </div>
            <select
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={convertForm.packageTier}
              onChange={(e) => setConvertForm({ ...convertForm, packageTier: e.target.value })}
            >
              {['STARTER', 'GROWTH', 'SCALE', 'CUSTOM'].map((tier) => (
                <option key={tier} value={tier}>
                  {tier}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button onClick={() => void convertToOpportunity()} disabled={busy}>
                {busy ? t('crm.converting') : t('crm.convert')}
              </Button>
              <Button variant="ghost" onClick={() => setConvertOpen(false)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        )}
      </Card>

      {/* Section 5 — Activity history */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t('crm.activityHistory', { count: lead.activities?.length ?? 0 })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {!lead.activities || lead.activities.length === 0 ? (
            <p className="py-4 text-center text-sm text-fg-muted">{t('crm.noActivities')}</p>
          ) : (
            <ul className="relative space-y-3 ms-3 border-s border-border ps-4 text-sm">
              {lead.activities.map((a) => (
                <li key={a.id} className="relative">
                  <span className="absolute -start-[1.4rem] top-1 grid h-5 w-5 place-items-center rounded-full bg-surface-muted text-xs">
                    {ACTIVITY_ICONS[a.activityType] ?? <Activity className="h-3 w-3" />}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">
                      {a.subject ?? a.activityType}
                    </p>
                    {a.isSystemEvent && (
                      <Badge tone="neutral" className="text-[10px]">
                        {t('crm.systemEvent', { defaultValue: 'system' })}
                      </Badge>
                    )}
                    <span className="text-xs text-fg-muted">
                      {new Date(a.activityTimestamp).toLocaleString()}
                    </span>
                  </div>
                  {a.outcome && (
                    <p className="text-xs text-fg-muted mt-1">{a.outcome}</p>
                  )}
                  {a.aiSummary && (
                    <p className="text-xs italic text-fg-muted mt-1">
                      {t('crm.aiSummary', { defaultValue: 'AI summary' })}: {a.aiSummary}
                    </p>
                  )}
                  {a.emailFrom && (
                    <p className="text-xs text-fg-muted mt-1">
                      <Mail className="inline h-3 w-3 me-1" />
                      {a.emailFrom} →{' '}
                      {(a.emailTo ?? []).join(', ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  wide,
  icon,
}: {
  label: string;
  value?: string | number | null;
  mono?: boolean;
  wide?: boolean;
  icon?: React.ReactNode;
}) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={`mt-0.5 inline-flex items-center gap-1 ${mono ? 'font-mono' : ''}`}>
        {icon}
        <span>{display}</span>
      </p>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-fg-muted">{label}</span>
      <select
        className="rounded border border-border bg-surface px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextareaField({
  label,
  value,
  onChange,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-fg-muted">{label}</span>
      <textarea
        className="rounded border border-border bg-surface px-3 py-2 text-sm"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
